import React, { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import queryString from 'query-string'
import { RECEPTOR_TYPE } from '@trustero/trustero-api-web/lib/agent/receptor_pb'
import { useListDirectoryScanSettings } from 'src/pages/Evidence/evidence.hooks'
import { INDEX_PAGE_WITH_FILTERS } from '../../IndexPage.constants'
import { FilterName, FilterParam } from '../FilterBar.types'
import { FilterDropdown } from './FilterDropdown'
import { getReceptorFilterValues } from './FilterDropdown.helpers'
import { LoadingFilterDropdown } from './FilterDropdown.components'
import { DropdownItemType } from './FilterDropdown.constants'
import { SourceFilterValues } from './SourceFilterDropdown'

export const DocumentScanFilterDropdown = React.memo(
  ({ isNested = false }: { isNested?: boolean }): JSX.Element => {
    const location = useLocation()
    const queryParams = queryString.parse(location.search, {
      arrayFormat: 'bracket',
    })
    const isPoliciesAutomatedScanTab =
      location.pathname.includes(INDEX_PAGE_WITH_FILTERS.POLICIES) &&
      queryParams.tab === 'automated-scan'
    const { data: directoryScanSettings, isLoading: isDirectoryLoading } =
      useListDirectoryScanSettings()

    const folderScanFilterValues = useMemo(
      () =>
        directoryScanSettings?.getSettingsList().map((setting) => ({
          value: setting.getId(),
          label: setting.getName() || setting.getRootPath(),
        })) || [],
      [directoryScanSettings],
    )

    const filterValues = useMemo(() => {
      const values: DropdownItemType[] = []

      if (!isPoliciesAutomatedScanTab) {
        values.push({
          value: SourceFilterValues.RECEPTOR,
          label: 'Receptor',
          nestedFilter: {
            filterParam: FilterParam.RECEPTOR,
            filterValues: getReceptorFilterValues(RECEPTOR_TYPE.RECEPTOR),
          },
        })
      }

      values.push({
        value: SourceFilterValues.DOCUMENT_MANAGEMENT_SYSTEM,
        label: 'Document Management System',
        nestedFilter: {
          filterParam: FilterParam.DOCUMENT_MANAGEMENT_SYSTEM,
          filterValues: getReceptorFilterValues(
            RECEPTOR_TYPE.COLLABORATION_SYSTEM,
          ),
        },
      })

      if (!isPoliciesAutomatedScanTab) {
        values.push({
          value: SourceFilterValues.WORKFLOW_MANAGEMENT_SYSTEM,
          label: 'Workflow Management System',
          nestedFilter: {
            filterParam: FilterParam.WORKFLOW_MANAGEMENT_SYSTEM,
            filterValues: getReceptorFilterValues(
              RECEPTOR_TYPE.WORKFLOW_PLATFORM,
            ),
          },
        })
      }

      values.push({
        value: 'folder_scan',
        label: 'Folder Scan',
        nestedFilter: {
          filterParam: FilterParam.FOLDER_SCAN,
          filterValues: folderScanFilterValues,
        },
      })

      return values
    }, [folderScanFilterValues, isPoliciesAutomatedScanTab])

    if (isDirectoryLoading) {
      return (
        <LoadingFilterDropdown
          filterName={FilterName.SOURCE}
          isNested={isNested}
        />
      )
    }

    return (
      <FilterDropdown
        filterParam={FilterParam.SOURCE}
        filterName={FilterName.SOURCE}
        filterValues={filterValues}
        hasNested
        includeParentFilter={true}
        isNested={isNested}
      />
    )
  },
)
